import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/api';
import { useConfirm } from '../../shared/ConfirmContext';
import { usePermissions } from '../../shared/permissions';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';

const ProfessionManagement = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();

  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [editingProfession, setEditingProfession] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  const { data: professions = [], isLoading, error } = useQuery({
    queryKey: ['professions'],
    queryFn: () => api.get('crew/api/professions/'),
    select: (data) => Array.isArray(data) ? data : (data.results || []),
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post('crew/api/professions/', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professions'] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => api.put(`crew/api/professions/${id}/`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professions'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`crew/api/professions/${id}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['professions'] }),
  });

  const openAddModal = () => {
    setEditingProfession(null);
    setFormData({ name: '', description: '' });
    createMutation.reset();
    updateMutation.reset();
    setShowModal(true);
  };

  const openEditModal = (profession) => {
    setEditingProfession(profession);
    setFormData({ name: profession.name, description: profession.description || '' });
    createMutation.reset();
    updateMutation.reset();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProfession(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingProfession) {
      updateMutation.mutate({ id: editingProfession.id, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = async (profession) => {
    const ok = await confirm(t('crew.professions.confirmDelete', { name: profession.name }));
    if (!ok) return;
    deleteMutation.mutate(profession.id);
  };

  const pageError = error?.message || deleteMutation.error?.message;
  const saveError = createMutation.error?.message || updateMutation.error?.message;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <PageShell pretitle={t('crew.overview')} title={t('crew.professions.title')}>
        <PageSection>
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('crew.overview')}
      title={t('crew.professions.title')}
      actions={(
        <>
          <Link to="/crew" className="btn btn-outline-secondary">
            <i className="ti ti-arrow-left me-1"></i>
            {t('crew.title')}
          </Link>
          {canManage && (
            <button className="btn btn-primary" onClick={openAddModal}>
              <i className="ti ti-plus me-1"></i>
              {t('crew.professions.add')}
            </button>
          )}
        </>
      )}
    >
      {pageError && (
        <div className="alert alert-danger mb-0" role="alert">
          {pageError}
        </div>
      )}

      <PageSection bodyClassName="p-0">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th>{t('crew.professions.name')}</th>
                <th>{t('crew.professions.description')}</th>
                {canManage && <th className="w-1">{t('common.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {professions.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 3 : 2} className="text-center text-muted py-4">
                    {t('crew.professions.noProfessions')}
                  </td>
                </tr>
              ) : (
                professions.map(profession => (
                  <tr key={profession.id}>
                    <td className="fw-medium">{profession.name}</td>
                    <td className="text-muted">{profession.description || '—'}</td>
                    {canManage && (
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            title={t('common.edit')}
                            onClick={() => openEditModal(profession)}
                          >
                            <i className="ti ti-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            title={t('common.delete')}
                            onClick={() => handleDelete(profession)}
                          >
                            <i className="ti ti-trash"></i>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </PageSection>

      {showModal && (
        <div className="modal modal-blur show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <form onSubmit={handleSubmit} className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingProfession ? t('crew.professions.edit') : t('crew.professions.add')}
                </h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body">
                {saveError && (
                  <div className="alert alert-danger mb-3" role="alert">{saveError}</div>
                )}
                <div className="mb-3">
                  <label className="form-label required">{t('crew.professions.name')}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('crew.professions.namePlaceholder')}
                    required
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">{t('crew.professions.description')}</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder={t('crew.professions.descriptionPlaceholder')}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary ms-auto" disabled={isSaving}>
                  {isSaving
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>{t('common.saving')}</>
                    : editingProfession ? t('common.save') : t('common.create')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showModal && <div className="modal-backdrop fade show"></div>}
    </PageShell>
  );
};

export default ProfessionManagement;
